import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import HeroSection from "./components/HeroSection";
import PricingSection from "./components/PricingSection";
import InfoSection from "./components/InfoSection";
import ProjectsSection from "./components/ProjectsSection";
import HackersSection from "./components/HackersSection";
import StatsSection from "./components/StatsSection";
import PageMeta from "@/components/base/PageMeta";

const siteUrl = import.meta.env.VITE_PUBLIC_SITE_URL || "https://hackerslodge.com";

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Hackers Lodge",
  url: siteUrl,
  description: "Hackers Lodge is a professional cybersecurity marketplace connecting clients with 90+ vetted freelance hackers worldwide for penetration testing, ethical hacking, vulnerability assessment, bug bounty, and cybersecurity services.",
  email: "support@hackerslodge.com",
  telephone: "+1-800-HACKERS",
  areaServed: "Worldwide",
  foundingDate: "2020",
  sameAs: [
    "https://twitter.com/hackerslodge",
    "https://t.me/hackerslodge",
    "https://github.com/hackerslodge",
    "https://reddit.com/r/hackerslodge",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: "support@hackerslodge.com",
    availableLanguage: ["English"],
  },
  actionableFeedbackPolicy: {
    "@type": "CreativeWork",
    url: `${siteUrl}/contact`,
  },
  correctionsPolicy: {
    "@type": "CreativeWork",
    url: `${siteUrl}/contact`,
  },
};

const homeSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Hackers Lodge",
  url: siteUrl,
  description: "Hackers Lodge connects you with 90+ vetted freelance hackers worldwide. Browse profiles, post projects, and hire professionals for penetration testing, database recovery, social engineering, mobile hacking, and more.",
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${siteUrl}/hackers?search={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
  publisher: {
    "@type": "Organization",
    name: "Hackers Lodge",
    url: siteUrl,
    email: "support@hackerslodge.com",
    sameAs: ["https://twitter.com/hackerslodge"],
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "How do I hire a professional hacker on Hackers Lodge?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Browse our directory of 90+ verified hackers, filter by specialty and country, view profiles and reviews, then submit a hire request through our secure platform. Our concierge team reviews requests and matches you with the best-qualified professional for your project.",
      },
    },
    {
      "@type": "Question",
      name: "What types of cybersecurity services are available?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Hackers Lodge offers penetration testing, ethical hacking, vulnerability assessment, social media recovery, database recovery, mobile hacking, email security, website security, cryptocurrency forensics, spy and surveillance, and custom cybersecurity services.",
      },
    },
    {
      "@type": "Question",
      name: "Is it safe to hire a hacker through Hackers Lodge?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Hackers Lodge uses escrow payment protection, verified hacker profiles with reviews and ratings, and a dedicated concierge team to manage communication. All transactions are confidential and protected by our platform policies.",
      },
    },
    {
      "@type": "Question",
      name: "What payment methods are accepted?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We accept cryptocurrency payments including Bitcoin (BTC), Ethereum (ETH), USDT, and other major cryptocurrencies. Escrow and wire transfer options are also available for qualifying projects.",
      },
    },
    {
      "@type": "Question",
      name: "How much does it cost to hire a hacker?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Rates start at $29 per hour and vary based on the hacker's experience, specialty, and project complexity. Project budgets typically range from $100 to $10,000+. You can browse hacker profiles to see their rates before submitting a request.",
      },
    },
    {
      "@type": "Question",
      name: "How long does a typical hacking project take?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Project timelines vary based on complexity. Simple tasks may be completed within 24-72 hours, while comprehensive penetration testing engagements can take 2-4 weeks. Each project's estimated delivery time is specified during the posting process.",
      },
    },
  ],
};

export default function Home() {
  return (
    <div className="min-h-screen bg-[#0d1117]">
      <PageMeta
        title="Hire Professional Freelance Hackers | Ethical Hacking &amp; Cybersecurity Marketplace"
        description="Browse 90+ vetted freelance hackers on Hackers Lodge. Post projects for penetration testing, database recovery, ethical hacking, bug bounty, social engineering, vulnerability assessment, and cybersecurity across network security, cloud security, application security, digital forensics, and incident response. Secure escrow payments with cybersecurity professionals worldwide."
        keywords="hire a hacker, professional hacker, freelance hacker, ethical hacking, penetration testing, cybersecurity marketplace, bug bounty, vulnerability assessment, hackers for hire, database recovery, security research, red team, blue team"
        canonicalUrl={`${siteUrl}/`}
        ogType="website"
        robots="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        schema={homeSchema}
      />
      {/* Organization Schema — separate injection for the global Organization JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <Header />
      <main className="pt-16 md:pt-20">
        <section className="bg-[#0d1117] text-center py-8 md:py-10 px-4">
          <h1 className="text-xl md:text-3xl font-light text-gray-200 max-w-3xl mx-auto leading-snug">
            Hackers Lodge — Professional Hacker Marketplace for Cybersecurity Services
          </h1>
          <p className="text-sm text-gray-500 mt-3 max-w-xl mx-auto">
            Browse 90+ vetted professional hackers worldwide. Post projects, get matched, and hire top cybersecurity talent.
          </p>
        </section>
        <HeroSection />
        <PricingSection />
        <InfoSection />
        <ProjectsSection />
        <HackersSection />
        <StatsSection />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      </main>
      <Footer />
    </div>
  );
}